import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useRouter } from 'next/router';
import { CONSENT_CATEGORIES, COOKIES_CONFIG, PATHS } from '@belong/common';
import { Button, Text } from '@belong/ui';
import {
  ConsentBanner,
  ConsentProvider,
  useLocation,
  usePolicyResolution,
  useSnapshot,
} from 'c15t/react';
import dynamic from 'next/dynamic';
import { parseCookies } from 'nookies';
import {
  c15tMode,
  c15tOverrides,
  CONSENT_DEV_TOOLS_ENABLED,
  CONSENT_DISABLED,
  RETENTION_PATHNAMES,
} from '../../constants';
import { STRINGS } from '../../strings';
import { setConsentSnapshot } from '../../consent-state';
import { updateConsentMode } from '../../google-consent-mode';
import { logConsentEvent } from '../../log';
import { buildConsentScripts, flushPixelQueue } from '../../utils';
import { ConsentDialog } from '../consent-dialog/consent-dialog';
import { ConsentErrorBoundary } from '../consent-error-boundary/consent-error-boundary';

/**
 * DevTools is loaded through `next/dynamic` so the panel stays out of the main
 * bundle, and out of production entirely, since the import only ever evaluates
 * when `CONSENT_DEV_TOOLS_ENABLED`.
 */
const DevTools = dynamic(
  () => import('c15t/react/devtools').then((mod) => mod.DevTools),
  { ssr: false },
);

/**
 * Mirrors the kernel snapshot for code outside React (the Consent Mode
 * scripts, the lead tracking) and logs the resolved policy once.
 */
function ConsentStateBridge() {
  const snapshot = useSnapshot();
  const resolution = usePolicyResolution();
  const location = useLocation();

  setConsentSnapshot(snapshot);

  const { policyPending, policyRule, translations } = snapshot;

  useEffect(() => {
    if (policyPending) {
      return;
    }

    logConsentEvent('init.resolved', {
      consentDisabled: CONSENT_DISABLED,
      overrides: c15tOverrides,
      status: resolution.status,
      policyId: policyRule.id,
      model: policyRule.model,
      countryCode: location?.countryCode,
      regionCode: location?.regionCode,
      language: translations?.language,
    });
  }, [policyPending, resolution.status, policyRule, location, translations]);

  return null;
}

function useWindowLoaded() {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (document.readyState === 'complete') {
      setLoaded(true);
      return;
    }

    const handleLoad = () => setLoaded(true);
    window.addEventListener('load', handleLoad);
    return () => window.removeEventListener('load', handleLoad);
  }, []);

  return loaded;
}

const BANNER_LINK_CLASSNAME =
  'font-semibold transition-colors duration-[100ms] hover:text-green';

const BANNER_CHOICE_CLASSNAME =
  'text-p1 leading-p1 text-center h-[35px] px-xs py-0 sm:h-auto sm:py-2xs border-2 border-solid rounded-small';

const BANNER_NEUTRAL_CHOICE_CLASSNAME =
  'border-navy text-white hover:border-navy hover:text-navy focus:border-navy focus:text-navy active:border-navy active:text-navy';

export function ConsentManager({ children }: { children: ReactNode }) {
  const router = useRouter();
  const windowLoaded = useWindowLoaded();

  const cookies = parseCookies();

  const trackingId = cookies[COOKIES_CONFIG.TRACKING_ID.name];

  const scripts = useMemo(
    () =>
      buildConsentScripts({
        trackingId,
        isRetentionPathname: RETENTION_PATHNAMES.has(router.pathname),
      }),
    [router.pathname, trackingId],
  );

  return (
    <ConsentProvider
      options={{
        mode: c15tMode,
        styles: false,
        overrides: c15tOverrides,

        consentCategories: [...CONSENT_CATEGORIES],
        scripts,

        scrollLock: false,
        legalLinks: {
          privacyPolicy: { href: PATHS.PRIVACY_POLICY, target: '_self' },
          termsOfService: { href: PATHS.TERMS, target: '_self' },
        },
        // Per-script load/block transitions. This is the signal that proves
        // gating actually happened, as opposed to the banner merely rendering.
        scriptLoader: {
          onDebug: (event) =>
            logConsentEvent('script.debug', {
              scriptId: event.scriptId,
              source: event.source,
              scope: event.scope,
              action: event.action,
              hasConsent: event.hasConsent,
              message: event.message,
            }),
        },
        callbacks: {
          onChoiceRecorded: ({ snapshot }) => {
            logConsentEvent('consent.set', {
              preferences: snapshot.effectivePermissions,
            });
          },
          // Change-only: never fires on hydration or auto-grants. This is the
          // hook a buffered lead-event queue should flush from.
          onPermissionsChanged: ({ snapshot }) => {
            setConsentSnapshot(snapshot);

            const { effectivePermissions } = snapshot;

            logConsentEvent('consent.changed', {
              preferences: effectivePermissions,
            });

            // Replay events raised before the visitor accepted.
            if (effectivePermissions.marketing) {
              flushPixelQueue('reddit');
              flushPixelQueue('enhanced-conversions');
            }
          },
          onBeforeConsentRevocationReload: ({ preferences }) => {
            updateConsentMode(preferences);
            logConsentEvent('consent.revocation_reload', { preferences });
          },
          onError: ({ error }) => {
            logConsentEvent('consent.error', { error });
          },
        },
      }}
    >
      <ConsentStateBridge />
      {windowLoaded && (
        <ConsentErrorBoundary surface="banner">
          <ConsentBanner.Root
            models={['opt-in', 'opt-out']}
            data-consent-banner
            className="font-sans w-auto p-0 rounded-none left-xs right-xs bottom-xs border-none z-[10002] translate-y-0 sm:left-md sm:right-auto sm:bottom-md"
          >
            <ConsentBanner.Card className="motion-safe:animate-consentBannerSlideUp sm:motion-safe:animate-consentBannerSlideIn shadow-card w-full max-w-none sm:max-w-xs bg-darker-navy rounded border border-solid border-navy">
              <ConsentBanner.Header className="bg-darker-navy px-xs pt-xs pb-0">
                <ConsentBanner.Description className="text-white">
                  <Text as="span" variant="p1">
                    {STRINGS.consentBannerDescription}{' '}
                    <a
                      href={PATHS.PRIVACY_POLICY}
                      className={BANNER_LINK_CLASSNAME}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {STRINGS.privacyPolicyLink}
                    </a>{' '}
                    {STRINGS.and}{' '}
                    <a
                      href={PATHS.TERMS}
                      className={BANNER_LINK_CLASSNAME}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {STRINGS.termsOfServiceLink}.
                    </a>
                  </Text>
                </ConsentBanner.Description>
              </ConsentBanner.Header>
              {/* Reject matches Accept in size: the CPPA regulations name an
                  asymmetric pair as a dark pattern. */}
              <ConsentBanner.Footer className="bg-darker-navy pt-[6px] pb-sm px-xs grid grid-cols-3 gap-xs border-none">
                <ConsentBanner.CustomizeButton asChild noStyle>
                  <Button
                    variant="outline"
                    buttonType="secondary"
                    size="fluid"
                    className={`${BANNER_CHOICE_CLASSNAME} ${BANNER_NEUTRAL_CHOICE_CLASSNAME}`}
                  >
                    {STRINGS.consentBannerCustomize}
                  </Button>
                </ConsentBanner.CustomizeButton>
                <ConsentBanner.RejectButton asChild noStyle>
                  <Button
                    variant="outline"
                    buttonType="secondary"
                    size="fluid"
                    className={`${BANNER_CHOICE_CLASSNAME} ${BANNER_NEUTRAL_CHOICE_CLASSNAME}`}
                  >
                    {STRINGS.consentBannerReject}
                  </Button>
                </ConsentBanner.RejectButton>
                <ConsentBanner.AcceptButton asChild noStyle>
                  <Button
                    variant="outline"
                    buttonType="secondary"
                    size="fluid"
                    className={BANNER_CHOICE_CLASSNAME}
                  >
                    {STRINGS.consentBannerAccept}
                  </Button>
                </ConsentBanner.AcceptButton>
              </ConsentBanner.Footer>
            </ConsentBanner.Card>
          </ConsentBanner.Root>
        </ConsentErrorBoundary>
      )}
      {windowLoaded && <ConsentDialog />}
      {CONSENT_DEV_TOOLS_ENABLED && <DevTools position="top-left" />}
      {/* The persistent opt-out link is rendered by the footer
          (`src/components/footer/footer.tsx`), below PRIVACY. */}
      {children}
    </ConsentProvider>
  );
}
